
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { getUserName } from "@/utils/userUtils";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

export const useAuthentication = () => {
  const [hasLocationAccess, setHasLocationAccess] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const { toast } = useToast();
  const { user, profile } = useAuth();

  // Enhanced authentication state detection with session validation
  useEffect(() => {
    const checkAuthenticationState = async () => {
      console.log('=== Authentication State Check ===');
      console.log('Supabase user:', !!user);
      console.log('Profile:', !!profile);
      console.log('Current shouldFetchData:', shouldFetchData);
      console.log('Current isAuthReady:', isAuthReady);
      
      const bypass = localStorage.getItem('location_bypass');
      const staffAuth = localStorage.getItem('staff_sms_auth');
      
      console.log('Location bypass exists:', !!bypass);
      console.log('Staff SMS auth exists:', !!staffAuth);
      
      let isAuthenticated = false;
      let authMethod = 'none';
      let currentUserName = null;
      
      // Step 1: Check Supabase authentication first with session validation
      if (user) {
        console.log('✅ Supabase user found, checking session...');
        
        // Validate the session is still active
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session && !error) {
          console.log('✅ Supabase session validated');
          isAuthenticated = true;
          authMethod = 'supabase';
          // Use profile info if available, otherwise fall back to user data
          currentUserName = profile?.full_name || profile?.email || user.email || 'User';
        } else {
          console.log('❌ Supabase session validation failed:', error?.message);
        }
      }
      // Step 2: Check location bypass with enhanced session monitoring
      else if (bypass) {
        try {
          const bypassData = JSON.parse(bypass);
          const now = new Date().getTime();
          const timeUntilExpiry = bypassData.expiry - now;
          
          console.log('📍 Location bypass data:', {
            expiry: new Date(bypassData.expiry).toISOString(),
            timeUntilExpiry,
            isValid: now < bypassData.expiry
          });
          
          if (now < bypassData.expiry) {
            console.log('✅ Authenticated via location bypass');
            isAuthenticated = true;
            authMethod = 'bypass';
            currentUserName = getUserName();
            
            // Show warning if session expiring soon (15 minutes)
            if (timeUntilExpiry <= 15 * 60 * 1000 && timeUntilExpiry > 0) {
              const minutes = Math.ceil(timeUntilExpiry / (60 * 1000));
              console.log(`⚠️ Location bypass expiring in ${minutes} minutes`);
            }
          } else {
            console.log('❌ Location bypass expired');
            localStorage.removeItem('location_bypass');
          }
        } catch (error) {
          console.error('❌ Error parsing location bypass:', error);
          localStorage.removeItem('location_bypass');
        }
      }
      // Step 3: Check SMS authentication (staff)
      if (!isAuthenticated && staffAuth) {
        try {
          const authData = JSON.parse(staffAuth);
          const now = new Date().getTime();
          console.log('SMS auth data:', { 
            authenticated: authData.authenticated, 
            expiry: authData.expiry, 
            now, 
            isValid: authData.authenticated && authData.expiry > now 
          });
          
          if (authData.authenticated && authData.expiry > now) {
            console.log('✅ Authenticated via SMS (staff level only)');
            isAuthenticated = true;
            authMethod = 'sms';
            // SECURITY: SMS auth always grants staff-level access only
            const employeeName = authData.userName || `${authData.employeeId || 'Staff User'}`;
            currentUserName = `${employeeName} (Staff)`;
          } else {
            console.log('❌ SMS auth expired or invalid');
            localStorage.removeItem('staff_sms_auth');
          }
        } catch (error) {
          console.error('Error checking staff auth:', error);
          localStorage.removeItem('staff_sms_auth');
        }
      }
      
      // SECURITY: Remove deprecated SMS admin authentication
      // Clean up any existing SMS admin auth tokens
      const oldSmsAdminAuth = localStorage.getItem('sms_admin_auth');
      if (oldSmsAdminAuth) {
        console.log('🔒 Cleaning up deprecated SMS admin auth');
        localStorage.removeItem('sms_admin_auth');
      }
      
      console.log('Final auth state:', { isAuthenticated, authMethod, currentUserName });
      
      // Set authentication states
      setHasLocationAccess(isAuthenticated);
      setUserName(currentUserName);
      
      // Set states immediately for authenticated users
      setShouldFetchData(isAuthenticated);
      setIsAuthReady(true);
      console.log('🔄 Setting shouldFetchData to:', isAuthenticated);
      console.log('🔄 Setting isAuthReady to: true');
    };

    checkAuthenticationState();
  }, [user, profile]);

  // Log when shouldFetchData changes
  useEffect(() => {
    console.log('🔄 shouldFetchData changed to:', shouldFetchData);
    console.log('🔄 isAuthReady:', isAuthReady);
  }, [shouldFetchData, isAuthReady]);

  return {
    hasLocationAccess,
    setHasLocationAccess,
    userName,
    setUserName,
    shouldFetchData,
    isAuthReady
  };
};
